import React, { useState, useRef, useEffect, useCallback } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { AdminService, CertificateService } from '../../services/APIService';
import { AppointmentCert, CERT_COMPONENTS as BASE_CERT_COMPONENTS } from '../CertTemplates';

const CERT_TYPES = [
  { id: 'appointment',  label: 'नियुक्ति प्रमाण पत्र',   labelEn: 'Appointment Certificate',  color: '#1a3a6b' },
  { id: 'registration', label: 'पंजीकरण प्रमाण पत्र',    labelEn: 'Registration Certificate', color: '#1a5c38' },
  { id: 'experience',   label: 'अनुभव प्रमाण पत्र',      labelEn: 'Experience Certificate',   color: '#7b1a1a' },
  { id: 'cultural',     label: 'सांस्कृतिक प्रमाण पत्र', labelEn: 'Cultural Certificate',     color: '#4a1a5c' },
  { id: 'idcard',       label: 'पहचान पत्र',              labelEn: 'ID Card',                  color: '#1a4a5c' },
];

function generateCertNo() {
  const year = new Date().getFullYear();
  const rand = Math.floor(100000 + Math.random() * 900000);
  return `KF/${year}/${rand}`;
}

const defaultForm = {
  type: 'appointment',
  name: '',
  role: '',
  fromDate: '',
  toDate: '',
  issueDate: new Date().toISOString().split('T')[0],
  certNo: generateCertNo(),
  eventName: '',
  place: '',
};

// ─── ID Card Template (400 × 620, portrait) ───────────────────────────────────

const IdCard = ({ data }) => (
  <div style={{ width: 400, height: 620, background: 'linear-gradient(160deg,#0a1e3d 0%,#1a3a6b 55%,#0d2651 100%)', position: 'relative', overflow: 'hidden', fontFamily: "'Segoe UI',Arial,sans-serif", color: '#fff', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
    {/* Border decorations */}
    <div style={{ position: 'absolute', top: 10, right: 10, bottom: 10, left: 10, border: '2px solid #c9a84c', borderRadius: 10, pointerEvents: 'none' }} />
    <div style={{ position: 'absolute', top: 15, right: 15, bottom: 15, left: 15, border: '1px solid rgba(201,168,76,0.3)', borderRadius: 8, pointerEvents: 'none' }} />

    {/* Header */}
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 28 }}>
      <img src="/Logo.jpeg" alt="Logo" style={{ width: 58, height: 58, borderRadius: '50%', border: '2px solid #c9a84c' }} />
      <div style={{ fontSize: 15, fontWeight: 900, letterSpacing: 2, color: '#fff', textTransform: 'uppercase', marginTop: 8 }}>KARIGAR FOUNDATION</div>
      <div style={{ fontSize: 9, color: '#c9a84c', letterSpacing: 1 }}>कला, संस्कृति और कौशल का सम्मान</div>
      <div style={{ width: 280, height: 1, background: '#c9a84c', marginTop: 10 }} />
      <div style={{ background: '#c9a84c', color: '#0d2651', fontSize: 10, fontWeight: 800, padding: '3px 22px', borderRadius: 20, marginTop: 8, letterSpacing: 1 }}>पहचान पत्र · IDENTITY CARD</div>
    </div>

    {/* Photo */}
    <div style={{ marginTop: 20 }}>
      {data.image ? (
        <img src={data.image} alt="User" style={{ width: 110, height: 140, objectFit: 'cover', border: '3px solid #c9a84c', borderRadius: 6, display: 'block' }} />
      ) : (
        <div style={{ width: 110, height: 140, border: '2px dashed #c9a84c', borderRadius: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#c9a84c', background: 'rgba(201,168,76,0.06)' }}>
          <svg width="38" height="38" fill="none" stroke="#c9a84c" strokeWidth="1.5" viewBox="0 0 24 24"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>
          <div style={{ fontSize: 10, marginTop: 6, letterSpacing: 1 }}>PHOTO</div>
        </div>
      )}
    </div>

    {/* Name & Info */}
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: 18, flex: 1 }}>
      <div style={{ fontSize: 22, fontWeight: 700, color: '#ffd700', letterSpacing: 1, textAlign: 'center', padding: '0 30px' }}>{data.name || 'नाम / Name'}</div>
      {data.role && (
        <div style={{ fontSize: 12, color: '#c9a84c', marginTop: 5, fontStyle: 'italic' }}>{data.role}</div>
      )}
      <div style={{ width: 240, height: 1, background: 'rgba(201,168,76,0.4)', marginTop: 14 }} />
      <div style={{ marginTop: 10, fontSize: 11, color: '#c9a84c', textAlign: 'center' }}>
        <span>ID: </span><strong style={{ color: '#ffd700', fontFamily: 'monospace', letterSpacing: 1 }}>{data.certNo}</strong>
      </div>
      <div style={{ marginTop: 6, fontSize: 10, color: '#aaa' }}>जारी दिनांक / Issue Date: <strong style={{ color: '#ccc' }}>{data.issueDate}</strong></div>
    </div>

    {/* Signature footer */}
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingBottom: 24, marginTop: 8 }}>
      <div style={{ width: 240, height: 1, background: 'rgba(201,168,76,0.4)', marginBottom: 10 }} />
      <div style={{ fontFamily: "'Dancing Script', cursive", fontSize: 22, color: '#ffd700', lineHeight: 1.1 }}>Rohit Kumar</div>
      <div style={{ fontSize: 10, color: '#c9a84c', marginTop: 4 }}>अध्यक्ष / संस्थापक — कारीगर फाउंडेशन</div>
    </div>
  </div>
);

const CERT_COMPONENTS = { ...BASE_CERT_COMPONENTS, idcard: IdCard };

// Dimensions per type
const CERT_DIMS = {
  idcard: { w: 400, h: 620, orientation: 'portrait' },
  _default: { w: 842, h: 595, orientation: 'landscape' },
};
const getDims = (type) => CERT_DIMS[type] || CERT_DIMS._default;

// ─── Main Component ────────────────────────────────────────────────────────────

const Certificates = () => {
  const [tab, setTab] = useState('generate');
  const [form, setForm] = useState(defaultForm);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState('');
  const [mode, setMode] = useState('manual');
  const [certList, setCertList] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [printData, setPrintData] = useState(null);
  const [listSearch, setListSearch] = useState('');
  const [downloadingId, setDownloadingId] = useState(null);
  // Certificate background image — stored in DB as binary, returned as base64 (same pattern as banners)
  const [certBgData, setCertBgData] = useState(null); // base64 data URL or null
  const [bgUploading, setBgUploading] = useState(false);
  const [bgMsg, setBgMsg] = useState('');
  // ID card photo — local only, not stored in DB
  const [uploadedImage, setUploadedImage] = useState(null);
  const previewRef = useRef(null);

  const loadUsers = useCallback(() => {
    setLoadingUsers(true);
    AdminService.getAllUsers()
      .then(data => setUsers(Array.isArray(data) ? data : data.data || data.users || []))
      .catch(() => setUsers([]))
      .finally(() => setLoadingUsers(false));
  }, []);

  const loadCertList = useCallback(() => {
    setLoadingList(true);
    CertificateService.getAll()
      .then(data => setCertList(Array.isArray(data) ? data : data.data || []))
      .catch(() => setCertList([]))
      .finally(() => setLoadingList(false));
  }, []);

  const loadCertBg = useCallback(() => {
    AdminService.getCertBgImage().then(data => setCertBgData(data.imageData || null));
  }, []);

  useEffect(() => { loadCertBg(); }, [loadCertBg]);

  useEffect(() => { loadUsers(); }, [loadUsers]);
  useEffect(() => { if (tab === 'list') loadCertList(); }, [tab, loadCertList]);

  const handleUserSelect = (userId) => {
    setSelectedUser(userId);
    const user = users.find(u => (u._id || u.id) === userId || String(u.id) === userId);
    if (user) setForm(f => ({ ...f, name: user.name || '', role: '' }));
  };

  const handleChange = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  const refreshCertNo = () => setForm(f => ({ ...f, certNo: generateCertNo() }));

  const handleIdPhotoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setUploadedImage(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleBgUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setBgUploading(true);
    setBgMsg('');
    try {
      await AdminService.setCertBgImage(file);
      await loadCertBg();
      setBgMsg('✅ Background image updated!');
    } catch (err) {
      setBgMsg('❌ ' + (err.message || 'Upload failed'));
    } finally {
      setBgUploading(false);
    }
  };

  const saveToDB = async () => {
    setSaving(true);
    setSavedMsg('');
    try {
      await CertificateService.create({
        cert_no: form.certNo,
        cert_type: form.type,
        recipient_name: form.name,
        user_id: selectedUser || null,
        role_title: form.role || null,
        event_name: form.eventName || null,
        from_date: form.fromDate || null,
        to_date: form.toDate || null,
        issue_date: form.issueDate,
        place: form.place || null,
      });
      setSavedMsg('✅ Certificate saved to database!');
    } catch (err) {
      setSavedMsg('❌ ' + (err.message || err.error || 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  const downloadPDF = async () => {
    setGenerating(true);
    setPrintData({ ...form, image: form.type === 'idcard' ? uploadedImage : certBgData });
    await new Promise(r => setTimeout(r, 300));
    await document.fonts.ready;
    try {
      const el = document.getElementById('admin-cert-print-area');
      if (!el) throw new Error('Print area not found');
      const { w, h, orientation } = getDims(form.type);
      const canvas = await html2canvas(el, { scale: 2, useCORS: true, allowTaint: true, backgroundColor: null });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({ orientation, unit: 'px', format: [w, h] });
      pdf.addImage(imgData, 'PNG', 0, 0, w, h);
      pdf.save(`${form.certNo.replace(/\//g, '-')}_${form.name || 'certificate'}.pdf`);
    } catch (err) {
      alert('PDF generation failed: ' + err.message);
    } finally {
      setGenerating(false);
      setPrintData(null);
    }
  };

  const downloadFromRecord = async (c) => {
    setDownloadingId(c.id);
    const recData = {
      type: c.cert_type,
      name: c.recipient_name,
      role: c.role_title || '',
      fromDate: c.from_date?.split('T')[0] || '',
      toDate: c.to_date?.split('T')[0] || '',
      issueDate: c.issue_date?.split('T')[0] || c.issue_date || '',
      certNo: c.cert_no,
      eventName: c.event_name || '',
      place: c.place || '',
      image: c.cert_type === 'idcard' ? null : certBgData,
    };
    setPrintData(recData);
    await new Promise(r => setTimeout(r, 300));
    await document.fonts.ready;
    try {
      const el = document.getElementById('admin-cert-print-area');
      if (!el) throw new Error('Print area not found');
      const { w, h, orientation } = getDims(c.cert_type);
      const canvas = await html2canvas(el, { scale: 2, useCORS: true, allowTaint: true, backgroundColor: null });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({ orientation, unit: 'px', format: [w, h] });
      pdf.addImage(imgData, 'PNG', 0, 0, w, h);
      pdf.save(`${c.cert_no.replace(/\//g, '-')}_${c.recipient_name}.pdf`);
    } catch (err) {
      alert('PDF generation failed: ' + err.message);
    } finally {
      setDownloadingId(null);
      setPrintData(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this certificate?')) return;
    setDeleteId(id);
    try {
      await CertificateService.delete(id);
      setCertList(prev => prev.filter(c => c.id !== id));
    } catch (err) {
      alert('Delete failed');
    } finally {
      setDeleteId(null);
    }
  };

  const CertComponent = CERT_COMPONENTS[form.type] || AppointmentCert;
  const certType = CERT_TYPES.find(c => c.id === form.type);
  const typeLabel = (type) => CERT_TYPES.find(c => c.id === type)?.labelEn || type;
  const { w: CERT_W, h: CERT_H } = getDims(form.type);
  const SCALE = form.type === 'idcard' ? 0.52 : 0.62;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Certificate Generator</h1>
          <p className="text-gray-500 text-sm mt-1">Generate & store official Karigar Foundation certificates</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setTab('generate')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${tab === 'generate' ? 'bg-blue-600 text-white' : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
            + Generate
          </button>
          <button onClick={() => setTab('list')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${tab === 'list' ? 'bg-blue-600 text-white' : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
            All Certificates
          </button>
        </div>
      </div>

      {/* ── GENERATE TAB ── */}
      {tab === 'generate' && (
        <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
          {/* Form */}
          <div className="xl:col-span-2 space-y-4">
            {/* Type */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Certificate Type</label>
              <div className="grid grid-cols-2 gap-2">
                {CERT_TYPES.map(ct => (
                  <button key={ct.id} onClick={() => { setForm(f => ({ ...f, type: ct.id })); if (ct.id !== 'idcard') setUploadedImage(null); }}
                    className={`p-2 rounded-lg border-2 text-xs font-medium transition-all text-left ${form.type === ct.id ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}>
                    <div>{ct.labelEn}</div>
                    <div className="text-gray-400 mt-0.5">{ct.label}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Fill Mode */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Fill Mode</label>
              <div className="flex gap-2 mb-3">
                {['manual', 'user'].map(m => (
                  <button key={m} onClick={() => setMode(m)}
                    className={`flex-1 py-1.5 rounded-lg text-sm font-medium border transition-all ${mode === m ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200'}`}>
                    {m === 'manual' ? 'Manual' : 'From User Data'}
                  </button>
                ))}
              </div>
              {mode === 'user' && (
                <select className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={selectedUser} onChange={e => handleUserSelect(e.target.value)} disabled={loadingUsers}>
                  <option value="">{loadingUsers ? 'Loading...' : '-- Select user --'}</option>
                  {users.map(u => <option key={u._id || u.id} value={u._id || u.id}>{u.name} ({u.email})</option>)}
                </select>
              )}
            </div>

            {/* Fields */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 space-y-3">
              <label className="block text-sm font-semibold text-gray-700">Certificate Details</label>

              <div>
                <label className="block text-xs text-gray-500 mb-1">Full Name *</label>
                <input name="name" value={form.name} onChange={handleChange} placeholder="Enter full name"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>

              {['appointment', 'experience', 'idcard'].includes(form.type) && (
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Role / Position (पद)</label>
                  <input name="role" value={form.role} onChange={handleChange} placeholder="e.g. Coordinator, Volunteer"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              )}

              {form.type === 'cultural' && (
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Event / Programme Name</label>
                  <input name="eventName" value={form.eventName} onChange={handleChange} placeholder="e.g. Kala Mahotsav 2025"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              )}

              {form.type === 'experience' && (
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">From Date</label>
                    <input type="date" name="fromDate" value={form.fromDate} onChange={handleChange}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">To Date</label>
                    <input type="date" name="toDate" value={form.toDate} onChange={handleChange}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                </div>
              )}

              <div>
                <label className="block text-xs text-gray-500 mb-1">Issue Date</label>
                <input type="date" name="issueDate" value={form.issueDate} onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>

              {form.type !== 'idcard' && (
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Place / Venue (स्थान)</label>
                  <input name="place" value={form.place} onChange={handleChange} placeholder="e.g. New Delhi, Lucknow"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              )}

              <div>
                <label className="block text-xs text-gray-500 mb-1">Certificate / ID No.</label>
                <div className="flex gap-2">
                  <input name="certNo" value={form.certNo} onChange={handleChange}
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  <button onClick={refreshCertNo} title="Regenerate"
                    className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-bold text-gray-600 transition-colors">↺</button>
                </div>
              </div>
            </div>

            {/* ID Card Photo (local only) */}
            {form.type === 'idcard' && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-semibold text-gray-700">ID Card Photo</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${uploadedImage ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                    {uploadedImage ? '✓ Loaded' : 'Not set'}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  {uploadedImage && (
                    <img src={uploadedImage} alt="ID card preview" style={{ width: 55, height: 70, objectFit: 'cover', borderRadius: 4, border: '1px solid #d1d5db', flexShrink: 0 }} />
                  )}
                  <label className="cursor-pointer flex-1 border-2 border-dashed rounded-lg p-3 text-center text-sm font-medium transition-colors border-gray-400 hover:border-blue-500 text-gray-600 hover:text-blue-600">
                    {uploadedImage ? '🔄 Replace Photo' : '📷 Upload Photo'}
                    <input type="file" accept="image/*" onChange={handleIdPhotoUpload} className="hidden" />
                  </label>
                </div>
                <p className="text-xs text-gray-400 mt-2">Photo is not stored in database — used only for PDF generation.</p>
              </div>
            )}

            {/* Certificate Background Image */}
            {form.type !== 'idcard' && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-semibold text-gray-700">Certificate Background</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${certBgData ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                    {certBgData ? '✓ Set' : 'Not set'}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  {certBgData && (
                    <img src={certBgData} alt="Current background" style={{ width: 72, height: 54, objectFit: 'cover', borderRadius: 6, border: '1px solid #d1d5db', flexShrink: 0 }} />
                  )}
                  <label className={`cursor-pointer flex-1 border-2 border-dashed rounded-lg p-3 text-center text-sm font-medium transition-colors ${bgUploading ? 'opacity-50 pointer-events-none border-gray-300 text-gray-400' : 'border-gray-400 hover:border-blue-500 text-gray-600 hover:text-blue-600'}`}>
                    {bgUploading ? '⏳ Uploading...' : certBgData ? '🔄 Replace Image' : '📤 Upload Background Image'}
                    <input type="file" accept="image/*" onChange={handleBgUpload} className="hidden" disabled={bgUploading} />
                  </label>
                </div>
                {bgMsg && <p className={`text-xs mt-2 font-medium ${bgMsg.startsWith('✅') ? 'text-green-600' : 'text-red-500'}`}>{bgMsg}</p>}
                <p className="text-xs text-gray-400 mt-2">Stored in database — same background used for all certificates. Upload once, update anytime.</p>
              </div>
            )}

            {/* Actions */}
            <div className="space-y-2">
              {form.type !== 'idcard' && (
                <>
                  <button onClick={saveToDB} disabled={saving || !form.name}
                    className={`w-full py-2.5 rounded-xl font-semibold text-sm transition-all ${saving || !form.name ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700 text-white shadow-md'}`}>
                    {saving ? '⏳ Saving...' : '💾 Save to Database'}
                  </button>
                  {savedMsg && <p className={`text-xs text-center font-medium ${savedMsg.startsWith('✅') ? 'text-green-600' : 'text-red-500'}`}>{savedMsg}</p>}
                </>
              )}
              <button onClick={downloadPDF} disabled={generating || !form.name}
                className={`w-full py-2.5 rounded-xl font-semibold text-sm transition-all ${generating || !form.name ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 text-white shadow-md'}`}>
                {generating ? '⏳ Generating PDF...' : '⬇ Download PDF'}
              </button>
              {!form.name && <p className="text-xs text-center text-red-400">Enter a name to enable actions</p>}
              {form.type === 'idcard' && <p className="text-xs text-center text-gray-400">ID Cards are for download only — not saved to database</p>}
            </div>
          </div>

          {/* Preview */}
          <div className="xl:col-span-3">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h2 className="text-sm font-semibold text-gray-700">Live Preview</h2>
                  <p className="text-xs text-gray-400">{certType?.labelEn} — {certType?.label}</p>
                </div>
                <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full font-mono">{form.certNo}</span>
              </div>
              <div style={{ width: '100%', overflowX: 'hidden' }}>
                <div style={{ transform: `scale(${SCALE})`, transformOrigin: 'top left', width: CERT_W, height: CERT_H }}>
                  <div ref={previewRef}><CertComponent data={{ ...form, image: form.type === 'idcard' ? uploadedImage : certBgData }} /></div>
                </div>
                <div style={{ height: `${CERT_H * SCALE}px` }} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── LIST TAB ── */}
      {tab === 'list' && (() => {
        const q = listSearch.trim().toLowerCase();
        const filtered = q
          ? certList.filter(c =>
              c.cert_no?.toLowerCase().includes(q) ||
              c.recipient_name?.toLowerCase().includes(q) ||
              c.user_email?.toLowerCase().includes(q) ||
              c.user_phone?.toLowerCase().includes(q) ||
              c.user_name?.toLowerCase().includes(q)
            )
          : certList;
        return (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-6 py-4 border-b border-gray-100">
              <h2 className="text-sm font-semibold text-gray-700 whitespace-nowrap">
                All Certificates ({filtered.length}{q ? ` of ${certList.length}` : ''})
              </h2>
              <div className="flex items-center gap-2 flex-1 max-w-sm">
                <input
                  value={listSearch}
                  onChange={e => setListSearch(e.target.value)}
                  placeholder="Search by name, email, phone, cert no…"
                  className="flex-1 border border-gray-300 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {listSearch && (
                  <button onClick={() => setListSearch('')} className="text-gray-400 hover:text-gray-600 text-xs">✕</button>
                )}
                <button onClick={loadCertList} className="text-xs text-blue-600 hover:underline whitespace-nowrap">Refresh</button>
              </div>
            </div>

            {loadingList ? (
              <div className="p-12 text-center text-gray-400">Loading...</div>
            ) : filtered.length === 0 ? (
              <div className="p-12 text-center text-gray-400">
                {certList.length === 0 ? 'No certificates generated yet.' : 'No results found.'}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wider">
                    <tr>
                      {['Cert No.', 'Type', 'Recipient', 'Linked User', 'Issue Date', 'Issued By', 'Actions'].map(h => (
                        <th key={h} className="px-4 py-3 text-left font-semibold">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filtered.map(c => (
                      <tr key={c.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3 font-mono text-blue-600 font-medium whitespace-nowrap">{c.cert_no}</td>
                        <td className="px-4 py-3">
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700">{typeLabel(c.cert_type)}</span>
                        </td>
                        <td className="px-4 py-3 font-medium text-gray-800">{c.recipient_name}</td>
                        <td className="px-4 py-3 text-gray-500">
                          <div>{c.user_name || <span className="text-gray-300">—</span>}</div>
                          {c.user_email && <div className="text-xs text-gray-400">{c.user_email}</div>}
                          {c.user_phone && <div className="text-xs text-gray-400">{c.user_phone}</div>}
                        </td>
                        <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{c.issue_date?.split('T')[0] || c.issue_date}</td>
                        <td className="px-4 py-3 text-gray-500">{c.issued_by_name || '—'}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <button onClick={() => downloadFromRecord(c)} disabled={downloadingId === c.id}
                              className="text-xs text-blue-600 hover:text-blue-800 font-medium transition-colors disabled:opacity-50">
                              {downloadingId === c.id ? '⏳' : '⬇ PDF'}
                            </button>
                            <button onClick={() => handleDelete(c.id)} disabled={deleteId === c.id}
                              className="text-xs text-red-500 hover:text-red-700 font-medium transition-colors disabled:opacity-50">
                              {deleteId === c.id ? 'Deleting…' : 'Delete'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })()}

      {/* Hidden full-size render area for html2canvas — never scaled */}
      {printData && (() => {
        const PrintComp = CERT_COMPONENTS[printData.type] || AppointmentCert;
        return (
          <div style={{ position: 'fixed', left: -9999, top: 0, zIndex: -1, pointerEvents: 'none' }}>
            <div id="admin-cert-print-area"><PrintComp data={printData} /></div>
          </div>
        );
      })()}
    </div>
  );
};

export default Certificates;
